import { guideIsContracted, guideStepIsContracted } from '@hue/contracts';
import type { GuideDefinition, GuideFacts, GuideMark, GuideStep } from './guide-types.js';

export interface Contracted {
  moduleKeys: readonly string[];
  featureKeys: readonly string[];
}

export function guideVisible(guide: GuideDefinition, contracted: Contracted): boolean {
  return guideIsContracted(guide.key, contracted);
}

/// Só o que a empresa contratou aparece: o roteiro não fala de tela que ela não tem.
export function stepsContracted(guide: GuideDefinition, contracted: Contracted): GuideStep[] {
  return guide.steps.filter((step) => guideStepIsContracted(guide.key, step.key, contracted));
}

export type StepState =
  | { kind: 'measured-done'; text: string }
  | { kind: 'measured-todo'; text: string }
  | { kind: 'measured-unavailable'; text: string }
  | { kind: 'confirmed'; by: string | null; on: string }
  | { kind: 'not-applicable' }
  | { kind: 'to-confirm'; text: string }
  | { kind: 'reference'; text: string };

const inMonth = (text: string, monthName: string) => text.replaceAll('{mes}', monthName);

/// A regra do ADR 0084, num lugar só: o que o sistema mede vale mais que qualquer confirmação; o
/// que ele não mede depende da afirmação de alguém. Fonte indisponível não autoriza confirmação
/// humana de um passo automático.
export function stepState(
  step: GuideStep,
  facts: GuideFacts,
  mark: GuideMark | undefined,
  monthName: string,
): StepState {
  if (step.appendix) return { kind: 'reference', text: step.action };
  const measured = step.measure?.done(facts) ?? null;
  if (step.measure && measured === true) return { kind: 'measured-done', text: step.measure.ok };
  if (step.measure && measured === false) {
    return { kind: 'measured-todo', text: inMonth(step.measure.todo(facts, monthName), monthName) };
  }
  if (step.measure) {
    return {
      kind: 'measured-unavailable',
      text: 'Não foi possível conferir este passo. A conclusão continua automática quando a consulta estiver disponível.',
    };
  }
  if (mark?.status === 'NOT_APPLICABLE') return { kind: 'not-applicable' };
  if (mark) return { kind: 'confirmed', by: mark.markedByName, on: mark.markedAt };
  return { kind: 'to-confirm', text: inMonth(step.action, monthName) };
}

export function stepDone(state: StepState): boolean {
  return (
    state.kind === 'measured-done' || state.kind === 'confirmed' || state.kind === 'not-applicable'
  );
}

/// O roteiro abre no primeiro passo que ainda pede algo; com tudo feito, no último.
export function firstPending(states: readonly StepState[], steps: readonly GuideStep[]): number {
  const index = states.findIndex((state, i) => !steps[i]!.appendix && !stepDone(state));
  if (index >= 0) return index;
  const last = steps.reduce((found, step, i) => (step.appendix ? found : i), 0);
  return last;
}

export function nextPending(
  from: number,
  states: readonly StepState[],
  steps: readonly GuideStep[],
): number {
  for (let i = from + 1; i < steps.length; i += 1) {
    if (!steps[i]!.appendix && !stepDone(states[i]!)) return i;
  }
  return Math.min(from + 1, steps.length - 1);
}
